import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api, API } from "../../lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, CartesianGrid,
} from "recharts";
import { BarChart3, Download, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PALETTE = ["#FF00F5", "#0033FF", "#7B2CFF", "#00D9FF", "#FFB800", "#FF6B35", "#00C896", "#E94B8B", "#7DD3FC", "#A78BFA", "#FACC15", "#34D399"];
const GRID = "rgba(255,255,255,0.06)";
const AXIS = "rgba(255,255,255,0.5)";

const tt = {
  contentStyle: { background: "#0F0F0F", border: "1px solid #FF00F5", color: "#fff", fontSize: 12 },
  itemStyle: { color: "#fff" }, labelStyle: { color: "#FF00F5", marginBottom: 4 },
};
const LEGEND_STYLE = { fontSize: 11, color: "#fff" };
const BAR_RADIUS = [4, 4, 0, 0];

const fmt$ = (n) => `$${(n || 0).toLocaleString()}`;
const STATUS_LABEL = {
  new: "New", under_review: "Under Review", contacted: "Contacted",
  approved: "Approved", closed: "Closed", archived: "Archived",
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/analytics/overview", { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/analytics/export.csv`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aawta-gala-analytics.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (loading || !data) {
    return <p className="text-white/60" data-testid="analytics-loading">Loading analytics…</p>;
  }

  return (
    <div className="space-y-10" data-testid="analytics-root">
      <div className="flex items-end justify-between flex-wrap gap-4 no-print">
        <div>
          <p className="text-overline text-aawta-pink mb-2 flex items-center gap-2"><BarChart3 size={14} /> ANALYTICS</p>
          <h1 className="font-display text-4xl text-white">Board-Ready Insights</h1>
          <p className="text-white/50 mt-2 text-sm">
            Live snapshot of fundraising progress, pipeline health, and community impact.
            Generated {new Date(data.generated_at).toLocaleString()}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="analytics-refresh-btn" onClick={load} className="btn-ghost py-2.5 px-4">
            <RefreshCw size={14} /> REFRESH
          </button>
          <button data-testid="analytics-export-btn" onClick={exportCsv} className="btn-ghost py-2.5 px-4">
            <Download size={14} /> CSV
          </button>
          <button data-testid="analytics-print-btn" onClick={() => window.print()} className="btn-pink">
            <Printer size={14} /> EXPORT PDF
          </button>
        </div>
      </div>

      {/* --- KPI Cards ----------------------------------------------------- */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <Kpi label="Total Raised" value={fmt$(data.totals.total_raised)} sub={`of ${fmt$(data.totals.total_goal)} · ${data.totals.pct}%`} accent />
        <Kpi label="Tickets Sold" value={data.capacity.tickets_sold} sub={`${data.capacity.pct_sold}% of ${data.capacity.total_capacity}`} />
        <Kpi label="VIP Tables Booked" value={data.capacity.vip_tables_reserved} sub={`${data.capacity.vip_tables_remaining} remaining`} />
        <Kpi label="Sponsors Secured" value={data.community.sponsors_secured} sub={`${data.community.industry_partnerships} industry partnerships`} />
      </section>

      {/* --- Fundraising Progress ----------------------------------------- */}
      <Section title="Fundraising Progress Over Time"
               subtitle="Cumulative dollars raised across Sponsorship, Scholarship and Strategic Partnership tracks.">
        <ChartFrame testId="chart-fundraising-timeline">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.fundraising_timeline}>
              <defs>
                <linearGradient id="g-sp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF00F5" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#FF00F5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g-sc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0033FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#0033FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g-pp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00D9FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#00D9FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="date" stroke={AXIS} fontSize={11} />
              <YAxis stroke={AXIS} fontSize={11} tickFormatter={fmt$} />
              <Tooltip {...tt} formatter={(v) => fmt$(v)} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Area type="monotone" dataKey="sponsorship" name="Sponsorship" stroke="#FF00F5" fill="url(#g-sp)" strokeWidth={2} />
              <Area type="monotone" dataKey="scholarship" name="Scholarship" stroke="#0033FF" fill="url(#g-sc)" strokeWidth={2} />
              <Area type="monotone" dataKey="partnership" name="Partnership" stroke="#00D9FF" fill="url(#g-pp)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Section>

      {/* --- Goals row --------------------------------------------------- */}
      <Section title="Goal Progress" subtitle="Real-time progress toward each fundraising track.">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          <GoalCard track="Sponsorship" g={data.goals.sponsorship} color="#FF00F5" testId="goal-sponsorship" />
          <GoalCard track="Scholarship" g={data.goals.scholarship} color="#0033FF" testId="goal-scholarship" />
          <GoalCard track="Strategic Partnership" g={data.goals.partnership} color="#00D9FF" testId="goal-partnership" />
          {data.goals.donations && (
            <GoalCard track="Donations" g={data.goals.donations} color="#FFB800" testId="goal-donations" />
          )}
          <TotalImpactCard totals={data.totals} testId="goal-total-impact" />
        </div>
      </Section>

      {/* --- Two-up: Tier donut + Weekly bars --------------------------- */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Sponsorship Revenue by Tier" subtitle="Estimated value of secured sponsors, by package.">
          <ChartFrame testId="chart-sponsor-tier" empty={data.sponsorship_by_tier.length === 0} emptyMsg="No sponsors recorded yet.">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.sponsorship_by_tier} dataKey="estimated_value" nameKey="tier"
                     cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
                  {data.sponsorship_by_tier.map((row, i) => (
                    <Cell key={row.tier} fill={PALETTE[i % PALETTE.length]} stroke="#0A0A0A" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip {...tt} formatter={(v, n, p) => [`${fmt$(v)} · ${p.payload.count} sponsor(s)`, p.payload.tier]} />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Section>

        <Section title="Weekly Submission Volume" subtitle="Last 12 weeks across all 12 intake forms.">
          <ChartFrame testId="chart-weekly-volume" empty={data.weekly_submissions.length === 0} emptyMsg="No submissions yet.">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.weekly_submissions}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="week" stroke={AXIS} fontSize={11} />
                <YAxis stroke={AXIS} fontSize={11} allowDecimals={false} />
                <Tooltip {...tt} />
                <Bar dataKey="total" fill="#FF00F5" name="Submissions" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Section>
      </div>

      {/* --- Capacity progress + Submission breakdown ------------------- */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Ticket Sales & Capacity" subtitle="Seats sold versus total capacity for the December 10, 2026 gala.">
          <CapacityCard cap={data.capacity} />
        </Section>

        <Section title="Submission Source Breakdown" subtitle="Where your community is engaging.">
          <ChartFrame testId="chart-source-breakdown" empty={data.source_donut.length === 0} emptyMsg="No submissions yet.">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.source_donut} dataKey="value" nameKey="name"
                     cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
                  {data.source_donut.map((row, i) => (
                    <Cell key={row.name} fill={PALETTE[i % PALETTE.length]} stroke="#0A0A0A" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip {...tt} />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </ChartFrame>
        </Section>
      </div>

      {/* --- Pipelines (3 stacked bars) ---------------------------------- */}
      <Section title="Pipeline Status" subtitle="Where each track sits in the triage workflow.">
        <div className="grid md:grid-cols-3 gap-4">
          <PipelineCard label="Sponsor Pipeline" pipeline={data.sponsor_pipeline} testId="pipeline-sponsor" />
          <PipelineCard label="Volunteer Pipeline" pipeline={data.volunteer_pipeline} testId="pipeline-volunteer" />
          <PipelineCard label="Vendor Pipeline" pipeline={data.vendor_pipeline} testId="pipeline-vendor" />
        </div>
      </Section>

      {/* --- Growth lines ------------------------------------------------ */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Volunteer Growth" subtitle="Cumulative volunteer registrations.">
          <GrowthChart series={data.volunteer_growth} color="#FF00F5" testId="chart-volunteer-growth" />
        </Section>
        <Section title="Vendor Growth" subtitle="Cumulative vendor applications.">
          <GrowthChart series={data.vendor_growth} color="#0033FF" testId="chart-vendor-growth" />
        </Section>
      </div>

      {/* --- Community impact final row --------------------------------- */}
      <Section title="Community Impact" subtitle="Cumulative outcomes powering the 'why' of the gala.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="community-impact-grid">
          <Kpi label="Workforce Participants" value={data.community.workforce_participants} sub="Served to date" />
          <Kpi label="Scholarships Awarded" value={data.community.scholarships_awarded} sub="Cumulative" />
          <Kpi label="Scholarships Funded" value={data.community.scholarships_funded} sub="Current cycle" />
          <Kpi label="Industry Partnerships" value={data.community.industry_partnerships} sub="Active" />
        </div>
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small building blocks                                              */
/* ------------------------------------------------------------------ */

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-aawta-pink/40 bg-aawta-pink/5" : "border-white/10 bg-aawta-charcoal/40"}`}>
      <p className="text-overline text-white/50">{label}</p>
      <p className="font-display text-3xl text-white mt-2">{value}</p>
      <p className="text-xs text-white/40 mt-1">{sub}</p>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-display text-2xl text-white">{title}</h2>
        <p className="text-white/50 text-sm">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function ChartFrame({ children, testId, empty, emptyMsg }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-aawta-charcoal/30 p-4">
      {empty ? (
        <div className="h-[280px] flex items-center justify-center text-white/40 text-sm">{emptyMsg}</div>
      ) : children}
    </div>
  );
}

function GoalCard({ track, g, color, testId }) {
  const pct = Math.min(100, g.pct || 0);
  return (
    <div className="p-5 border border-white/10 bg-aawta-charcoal/40" data-testid={testId}>
      <p className="text-overline text-white/50">{track}</p>
      <p className="font-display text-3xl text-white mt-2">{fmt$(g.raised)}</p>
      <p className="text-xs text-white/40">of {fmt$(g.goal)} goal</p>
      <div className="mt-3 h-2 bg-white/10 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <p className="text-xs mt-2" style={{ color }}>{pct}% achieved</p>
    </div>
  );
}

function TotalImpactCard({ totals, testId }) {
  const pct = Math.min(100, totals.pct || 0);
  return (
    <div className="p-5 border border-aawta-pink/40 bg-aawta-pink/5" data-testid={testId}>
      <p className="text-overline text-aawta-pink">Total Impact</p>
      <p className="font-display text-3xl text-white mt-2">{fmt$(totals.total_raised)}</p>
      <p className="text-xs text-white/40">of {fmt$(totals.total_goal)} · incl. {fmt$(totals.in_kind_total || 0)} in-kind</p>
      <div className="mt-3 h-2 bg-white/10 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-aawta-pink to-[#FFB800] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs mt-2 text-aawta-pink">{totals.pct}% achieved</p>
    </div>
  );
}

function CapacityCard({ cap }) {
  const pct = Math.min(100, cap.pct_sold || 0);
  const vipPct = cap.vip_tables_available
    ? Math.min(100, (cap.vip_tables_reserved / cap.vip_tables_available) * 100) : 0;
  return (
    <div className="border border-white/10 bg-aawta-charcoal/30 p-6 space-y-6" data-testid="capacity-card">
      <div>
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="text-overline text-white/50">Tickets Sold</p>
            <p className="font-display text-3xl text-white">{cap.tickets_sold} <span className="text-white/40 text-base">/ {cap.total_capacity}</span></p>
          </div>
          <p className="text-aawta-pink font-mono text-2xl">{cap.pct_sold}%</p>
        </div>
        <div className="h-3 bg-white/10 overflow-hidden">
          <div className="h-full bg-aawta-pink transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-white/40 mt-1">{cap.seats_remaining} seats remaining</p>
      </div>
      <div>
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="text-overline text-white/50">VIP Tables</p>
            <p className="font-display text-3xl text-white">{cap.vip_tables_reserved} <span className="text-white/40 text-base">/ {cap.vip_tables_available}</span></p>
          </div>
          <p className="text-aawta-pink font-mono text-2xl">{Math.round(vipPct)}%</p>
        </div>
        <div className="h-3 bg-white/10 overflow-hidden">
          <div className="h-full bg-aawta-pink transition-all" style={{ width: `${vipPct}%` }} />
        </div>
        <p className="text-xs text-white/40 mt-1">{cap.vip_tables_remaining} tables remaining</p>
      </div>
    </div>
  );
}

function PipelineCard({ label, pipeline, testId }) {
  const total = pipeline.total || 1;
  return (
    <div className="border border-white/10 bg-aawta-charcoal/40 p-5" data-testid={testId}>
      <p className="text-overline text-white/50">{label}</p>
      <p className="font-display text-3xl text-white mt-2">{pipeline.total}</p>
      <p className="text-xs text-white/40 mb-4">submissions in pipeline</p>
      <div className="space-y-2">
        {pipeline.stages.map((s, i) => {
          const pct = (s.count / total) * 100;
          return (
            <div key={s.status}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-white/60">{STATUS_LABEL[s.status]}</span>
                <span className="font-mono text-white/80">{s.count}</span>
              </div>
              <div className="h-1.5 bg-white/5 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: PALETTE[i % PALETTE.length] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GrowthChart({ series, color, testId }) {
  return (
    <ChartFrame testId={testId} empty={series.length === 0} emptyMsg="No data yet — first signups will appear here.">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={series}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="date" stroke={AXIS} fontSize={11} />
          <YAxis stroke={AXIS} fontSize={11} allowDecimals={false} />
          <Tooltip {...tt} />
          <Line type="monotone" dataKey="total" stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} name="Cumulative" />
        </LineChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
